/**
 * Sports Tabs Component
 * Horizontal scrollable sports selector with active state
 * Shows active sport from sidebar with proper color scheme
 */

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';
import SportsSVGIcons from '@/components/sports/SportsSVGIcons';
import styles from './SportsTabs.module.css';

// Top navigation tabs
const topTabsData = [
  { 
    id: 'in-play', 
    name: 'In Play', 
    path: '/sports/in-play',
    icon: 'popular'
  },
  { 
    id: 'today', 
    name: 'Today', 
    path: '/sports/today',
    icon: 'popular'
  },
  { 
    id: 'tomorrow', 
    name: 'Tomorrow', 
    path: '/sports/tomorrow',
    icon: 'popular'
  },
  { 
    id: 'my-bets', 
    name: 'My Bets', 
    path: '/sports/my-bets',
    icon: 'popular'
  },
  { 
    id: 'all-sports', 
    name: 'All Sports', 
    path: '/sports/all-sports',
    icon: 'popular'
  },
];

// Sports data with IDs and image paths (matching old codebase)
const sportsData = [
  { 
    id: 'popular', 
    name: 'Popular', 
    icon: null, 
    path: '/sports/popular', 
    sportId: null,
    type: 'svg'
  },
  { 
    id: 'cricket', 
    name: 'Cricket', 
    icon: '/assets/images/sports-matches/cricket.svg', 
    path: '/sports/in-play/cricket', 
    sportId: '4',
    type: 'image'
  },
  { 
    id: 'soccer', 
    name: 'Soccer', 
    icon: '/assets/images/sports-matches/football.svg', 
    path: '/sports/in-play/soccer', 
    sportId: '1',
    type: 'image'
  },
  { 
    id: 'tennis', 
    name: 'Tennis', 
    icon: '/assets/images/sports-matches/tennis.svg', 
    path: '/sports/in-play/tennis', 
    sportId: '2',
    type: 'image'
  },
  { 
    id: 'mma', 
    name: 'Mixed Martial Arts', 
    icon: '/assets/images/sports-matches/mma.svg', 
    path: '/sports/in-play/mma', 
    sportId: '26420387',
    type: 'image'
  },
  { 
    id: 'golf', 
    name: 'Golf', 
    icon: '/assets/images/sports-matches/golf.svg', 
    path: '/sports/in-play/golf', 
    sportId: '3',
    type: 'image'
  },
  { 
    id: 'boxing', 
    name: 'Boxing', 
    icon: '/assets/images/sports-matches/mma.svg', 
    path: '/sports/in-play/boxing', 
    sportId: '6',
    type: 'image'
  },
  { 
    id: 'american-football', 
    name: 'American Football', 
    icon: '/assets/images/sports-matches/american.svg', 
    path: '/sports/in-play/american-football', 
    sportId: '6423',
    type: 'image'
  },
  { 
    id: 'baseball', 
    name: 'Baseball', 
    icon: '/assets/images/sports-matches/baseball.svg', 
    path: '/sports/in-play/baseball', 
    sportId: '7511',
    type: 'image'
  },
  { 
    id: 'basketball', 
    name: 'Basketball', 
    icon: '/assets/images/sports/basketball.webp', 
    path: '/sports/in-play/basketball', 
    sportId: '7522',
    type: 'image'
  },
  { 
    id: 'rugby-league', 
    name: 'Rugby League', 
    icon: '/assets/images/sports-matches/rugby.svg', 
    path: '/sports/in-play/rugby-league', 
    sportId: '1477',
    type: 'image'
  },
  { 
    id: 'rugby-union', 
    name: 'Rugby Union', 
    icon: '/assets/images/sports-matches/rughbyunion.svg', 
    path: '/sports/in-play/rugby-union', 
    sportId: '5',
    type: 'image'
  },
  { 
    id: 'horse-racing', 
    name: 'Horse Racing', 
    icon: '/assets/images/sports-matches/horse.svg', 
    path: '/sports/in-play/horse-racing', 
    sportId: '7',
    type: 'image'
  },
  { 
    id: 'greyhound-racing', 
    name: 'Greyhound Racing', 
    icon: '/assets/images/sports-matches/grey.svg', 
    path: '/sports/in-play/greyhound-racing', 
    sportId: '4339',
    type: 'image'
  },
  { 
    id: 'motorsport', 
    name: 'Motor Sport', 
    icon: '/assets/images/sports-matches/motorsport.svg', 
    path: '/sports/in-play/motorsport', 
    sportId: '8',
    type: 'image'
  },
  { 
    id: 'snooker', 
    name: 'Snooker', 
    icon: '/assets/images/sports-matches/snooker.svg', 
    path: '/sports/in-play/snooker', 
    sportId: '6422',
    type: 'image'
  },
  { 
    id: 'darts', 
    name: 'Darts', 
    icon: '/assets/images/sports-matches/dart.svg', 
    path: '/sports/in-play/darts', 
    sportId: '3503',
    type: 'image'
  },
  { 
    id: 'gaelic-games', 
    name: 'Gaelic Games', 
    icon: '/assets/images/sports-matches/gaelic.svg', 
    path: '/sports/in-play/gaelic-games', 
    sportId: '2152880',
    type: 'image'
  },
  { 
    id: 'ice-hockey', 
    name: 'Ice Hockey', 
    icon: '/assets/images/sports-matches/ice-hockey.svg', 
    path: '/sports/in-play/ice-hockey', 
    sportId: '7512',
    type: 'image'
  },
];

const SportsTabs = ({ currentSport, sportId, currentTab = 'in-play', hideSportsTabs = false }) => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const containerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const touchStartRef = useRef({ x: 0, scrollLeft: 0, isDragging: false });
  const clickBlockedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateMaxScroll = () => {
      const containerWidth = container.offsetWidth;
      const scrollContent = container.querySelector('[data-scroll-track]');
      if (scrollContent) {
        const scrollWidth = scrollContent.scrollWidth;
        setMaxScroll(Math.max(0, scrollWidth - containerWidth));
      }
    };

    updateMaxScroll();
    window.addEventListener('resize', updateMaxScroll);

    // Desktop wheel scrolling
    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const scrollSensitivity = 1;
      const delta = (e.deltaX !== 0 ? e.deltaX : e.deltaY) * scrollSensitivity;
      setScrollPosition((prev) => {
        const newPosition = prev + delta;
        return Math.max(0, Math.min(newPosition, maxScroll || 0));
      });
    };

    // Touch event handlers for mobile
    const handleTouchStart = (e) => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        scrollLeft: scrollPosition,
        isDragging: false,
      };
    };

    const handleTouchMove = (e) => {
      if (!touchStartRef.current.x) return;
      
      const deltaX = touchStartRef.current.x - e.touches[0].clientX;
      
      // If moved more than 5px, consider it a drag
      if (Math.abs(deltaX) > 5) {
        touchStartRef.current.isDragging = true;
      }
      
      const newPosition = touchStartRef.current.scrollLeft + deltaX;
      setScrollPosition(Math.max(0, Math.min(newPosition, maxScroll || 0)));
    };

    const handleTouchEnd = (e) => {
      // Prevent click if it was a drag
      if (touchStartRef.current.isDragging) {
        clickBlockedRef.current = true;
        // Reset after a short delay to allow click event to check it
        setTimeout(() => {
          clickBlockedRef.current = false;
        }, 300);
      }
      touchStartRef.current = { x: 0, scrollLeft: 0, isDragging: false };
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', updateMaxScroll);
    };
  }, [maxScroll, scrollPosition]);

  const handleSportClick = (sport) => {
    // Navigate to current tab with selected sport
    if (sport.id === 'all-sports') {
      navigate('/sports/all-sports');
    } else if (sport.id === 'popular') {
      navigate('/sports/popular');
    } else {
      navigate(`/sports/${currentTab}/${sport.id}`);
    }
  };

  const handleTopTabClick = (tab) => {
    // Navigate to tab
    if (tab.id === 'all-sports') {
      navigate('/sports/all-sports');
    } else if (tab.id === 'my-bets') {
      navigate('/sports/my-bets');
    } else {
      // For in-play, today, tomorrow - navigate to tab only (no sport param)
      // This will show all sports in the market grid for that tab
      navigate(`/sports/${tab.id}`);
    }
  };

  const location = useLocation();
  const currentPath = location.pathname;
  
  // Check if we're on all-sports route (either /sports/all-sports or /sports/:tab/all-sports)
  const isOnAllSportsRoute = currentPath === '/sports/all-sports' || currentPath.includes('/all-sports');
  // Check if we're on my-bets route
  const isOnMyBetsRoute = currentPath === '/sports/my-bets' || currentPath.includes('/my-bets');
  // Check if we're on match detail route - should not show tabs
  const isMatchDetailRoute = currentPath.includes('/sports/markets/');
  const shouldHideSportsTabs = hideSportsTabs || isOnAllSportsRoute || isOnMyBetsRoute || isMatchDetailRoute;

  return (
    <div className={styles.sportsTabsContainer}>
      {/* Top Navigation Tabs */}
      <div className={styles.topTabsContainer}>
        {topTabsData
          .filter((tab) => {
            // Hide "All Sports" tab when on my-bets route
            if (tab.id === 'all-sports' && isOnMyBetsRoute) {
              return false;
            }
            return true;
          })
          .map((tab) => {
            let isActive = false;
            if (tab.id === 'all-sports') {
              // All Sports tab is active when on all-sports route
              isActive = isOnAllSportsRoute || currentPath === '/sports/all-sports';
            } else if (tab.id === 'my-bets') {
              // My Bets tab is active when on my-bets route
              isActive = isOnMyBetsRoute || currentTab === 'my-bets';
            } else {
              // Other tabs are active when currentTab matches and NOT on all-sports or my-bets route
              isActive = !isOnAllSportsRoute && !isOnMyBetsRoute && currentTab === tab.id;
            }
            
            return (
              <div
                key={tab.id}
                className={`${styles.topTab} ${isActive ? styles.active : ''}`}
                onClick={() => handleTopTabClick(tab)}
              >
                <div className={styles.topTabContent}>
                  <span className={styles.topTabName}>{tab.name}</span>
                </div>
              </div>
            );
          })}
      </div>

      {/* Sports Tabs - Hide when on /sports/all-sports or /sports/my-bets route */}
      {!shouldHideSportsTabs && (
        <div
          ref={containerRef}
          className={styles.scrollContainer}
          onMouseDown={(e) => (e.currentTarget.style.cursor = 'grabbing')}
          onMouseUp={(e) => (e.currentTarget.style.cursor = 'grab')}
          onMouseLeave={(e) => (e.currentTarget.style.cursor = 'grab')}
        >
          <div
            className={styles.scrollTrack}
            data-scroll-track
            style={{ transform: `translateX(-${scrollPosition}px)` }}
          >
            {sportsData.map((sport) => {
              const isActive = currentSport === sport.id;
              
              return (
                <div
                  key={sport.id}
                  className={`${styles.sportCard} ${isActive ? styles.active : ''}`}
                  onClick={() => {
                    // Prevent click if touch was a drag
                    if (!clickBlockedRef.current) {
                      handleSportClick(sport);
                    }
                  }}
                >
                  <div className={styles.sportContent}>
                    <SportsSVGIcons 
                      sportId={sport.id} 
                      size={isMobile ? "18px" : "24px"}
                      className={styles.sportIconImage}
                    />
                    <span className={styles.sportName}>{sport.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SportsTabs;
